<template>
  <div class="suivi mt-4">
    <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
      <h4 class="section-title mb-0"><i class="bi bi-journal-medical"></i> Suivi du patient</h4>
      <div class="d-flex gap-2 flex-wrap">
        <button v-if="peutSuivre && !d.cloturee" class="btn btn-sm btn-outline-danger" @click="constantes = true"><i class="bi bi-heart-pulse me-1"></i>Constantes</button>
        <button v-if="peutSuivre && !d.cloturee" class="btn btn-sm btn-outline-secondary" @click="ouvrirLit"><i class="bi bi-arrow-left-right me-1"></i>Changer de lit</button>
        <button v-if="peutCompteRendu" class="btn btn-sm btn-outline-primary" @click="ouvrirCompteRendu"><i class="bi bi-file-earmark-medical me-1"></i>Compte-rendu de sortie</button>
      </div>
    </div>

    <!-- Nouvelle entrée -->
    <div v-if="peutSuivre && !d.cloturee" class="card card-suivi p-2 mb-3">
      <div class="row g-2 align-items-end">
        <div class="col-md-3">
          <select v-model="f.type" class="form-select form-select-sm">
            <option v-for="(lib, code) in typesSaisie" :key="code" :value="code">{{ lib }}</option>
          </select>
        </div>
        <template v-if="f.type === 'MEDICAMENT'">
          <div class="col-md-3"><input v-model="f.medicament" class="form-control form-control-sm" placeholder="Médicament"></div>
          <div class="col-md-2"><input v-model="f.dose" class="form-control form-control-sm" placeholder="Dose"></div>
          <div class="col-md-2">
            <select v-model="f.voie" class="form-select form-select-sm">
              <option value="">Voie</option>
              <option v-for="v in ['Orale', 'IV', 'IM', 'SC', 'Perfusion', 'Rectale', 'Locale']" :key="v">{{ v }}</option>
            </select>
          </div>
        </template>
        <div :class="f.type === 'MEDICAMENT' ? 'col-md-2' : 'col-md-7'">
          <input v-model="f.texte" class="form-control form-control-sm" :placeholder="f.type === 'MEDICAMENT' ? 'Remarque' : 'Texte'" @keyup.enter="ajouter">
        </div>
        <div class="col-md-auto"><button class="btn btn-sm btn-primary" :disabled="enCours" @click="ajouter">Ajouter</button></div>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-lg-8">
        <ul class="list-unstyled timeline mb-0">
          <li v-for="s in d.suivi" :key="s.id" class="mb-2">
            <div class="d-flex justify-content-between small">
              <span><span class="badge me-1" :class="badge(s.type)">{{ s.libelleType }}</span><strong>{{ s.faitLe }}</strong></span>
              <span class="text-muted">{{ s.auteur }}</span>
            </div>
            <div v-if="s.medicament"><strong>{{ s.medicament }}</strong> {{ s.dose }} <span class="text-muted">{{ s.voie }}</span></div>
            <div v-if="s.texte" class="texte">{{ s.texte }}</div>
          </li>
          <li v-if="!d.suivi.length" class="text-muted small">Aucune entrée dans la feuille de suivi.</li>
        </ul>
      </div>
      <div class="col-lg-4">
        <div class="card card-suivi p-2">
          <strong class="small mb-1">Constantes du séjour</strong>
          <table class="table table-sm small mb-0">
            <tbody>
              <tr v-for="c in d.constantes" :key="c.id">
                <td class="text-nowrap">{{ c.prisLe.slice(5) }}</td>
                <td>
                  <span v-if="c.tensionSystolique">TA {{ c.tensionSystolique }}/{{ c.tensionDiastolique }} </span>
                  <span v-if="c.temperature != null">{{ String(c.temperature).replace('.', ',') }}°C </span>
                  <span v-if="c.pouls">P {{ c.pouls }} </span>
                  <span v-if="c.saturation != null">SpO₂ {{ c.saturation }}%</span>
                  <span v-for="a in c.alertes" :key="a" class="badge bg-danger ms-1">{{ a }}</span>
                </td>
              </tr>
              <tr v-if="!d.constantes.length"><td class="text-muted">Aucune.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <constantes-modal :visible="constantes" titre="patient hospitalisé" :cible="{ hospitalisation: Number(hospitalisationId) }" @fermer="constantes = false" @enregistre="charger" />

    <!-- Changer de lit -->
    <div v-if="changementLit" class="modal-backdrop-custom" @click.self="changementLit = null">
      <div class="modal-card card shadow" style="max-width: 480px">
        <div class="card-header d-flex justify-content-between"><h5 class="mb-0">Changer de lit</h5><button class="btn-close" @click="changementLit = null"></button></div>
        <div class="card-body">
          <p class="small text-muted">Lit actuel : {{ d.lit ? `${d.lit.numero} (chambre ${d.lit.chambre})` : '—' }}</p>
          <label class="form-label">Nouveau lit</label>
          <select v-model="changementLit.lit" class="form-select mb-2">
            <option v-for="l in d.litsLibres" :key="l.id" :value="l.id">Lit {{ l.numero }} — chambre {{ l.chambre }}</option>
          </select>
          <div v-if="!d.litsLibres.length" class="alert alert-warning py-2 small">Aucun lit libre.</div>
          <input v-model="changementLit.motif" class="form-control" placeholder="Motif (isolement, demande du patient…)">
        </div>
        <div class="card-footer text-end">
          <button class="btn btn-secondary me-2" @click="changementLit = null">Annuler</button>
          <button class="btn btn-primary" :disabled="!changementLit.lit" @click="changerLit">Transférer</button>
        </div>
      </div>
    </div>

    <!-- Compte-rendu de sortie -->
    <div v-if="cr" class="modal-backdrop-custom" @click.self="cr = null">
      <div class="modal-card card shadow">
        <div class="card-header d-flex justify-content-between"><h5 class="mb-0">Compte-rendu de sortie</h5><button class="btn-close" @click="cr = null"></button></div>
        <div class="card-body">
          <div class="row g-2">
            <div class="col-md-6"><label class="form-label small mb-1">Diagnostic d'entrée</label><input v-model="cr.diagnosticEntree" class="form-control"></div>
            <div class="col-md-6"><label class="form-label small mb-1">Diagnostic de sortie</label><input v-model="cr.diagnosticSortie" class="form-control"></div>
            <div class="col-12"><label class="form-label small mb-1">Résumé du séjour</label><textarea v-model="cr.resume" rows="4" class="form-control"></textarea></div>
            <div class="col-12"><label class="form-label small mb-1">Examens réalisés</label><textarea v-model="cr.examensRealises" rows="2" class="form-control"></textarea></div>
            <div class="col-md-6"><label class="form-label small mb-1">Traitement de sortie</label><textarea v-model="cr.traitementSortie" rows="3" class="form-control"></textarea></div>
            <div class="col-md-6"><label class="form-label small mb-1">Consignes au patient</label><textarea v-model="cr.consignes" rows="3" class="form-control"></textarea></div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Mode de sortie</label>
              <select v-model="cr.modeSortie" class="form-select">
                <option v-for="(lib, code) in modes" :key="code" :value="code">{{ lib }}</option>
              </select>
            </div>
            <div class="col-md-4" v-if="cr.modeSortie === 'TRANSFERT'"><label class="form-label small mb-1">Établissement</label><input v-model="cr.etablissementTransfert" class="form-control"></div>
            <div class="col-md-4"><label class="form-label small mb-1">Rendez-vous de contrôle</label><input v-model="cr.rendezVousControle" type="date" class="form-control"></div>
          </div>
          <div v-if="erreur" class="alert alert-danger mt-3 mb-0 py-2">{{ erreur }}</div>
        </div>
        <div class="card-footer d-flex justify-content-end gap-2">
          <button v-if="cr.id" class="btn btn-outline-secondary" @click="pdfCompteRendu"><i class="bi bi-file-earmark-pdf me-1"></i>PDF</button>
          <button class="btn btn-secondary" @click="cr = null">Fermer</button>
          <button class="btn btn-primary" :disabled="enCours" @click="enregistrerCompteRendu">Enregistrer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import ConstantesModal from '@/components/accueil/ConstantesModal.vue'

export default {
  name: 'SuiviHospitalisation',
  components: { ConstantesModal },
  props: { hospitalisationId: { type: [Number, String], required: true } },
  data() {
    return {
      d: { suivi: [], constantes: [], types: {}, lit: null, litsLibres: [], cloturee: false },
      f: { type: 'VISITE', texte: '', medicament: '', dose: '', voie: '' },
      constantes: false, changementLit: null, cr: null, modes: {}, enCours: false, erreur: ''
    }
  },
  computed: {
    peutSuivre() { return this.$store.getters.hasPermission('HOSP_SUIVI') },
    peutCompteRendu() { return this.$store.getters.hasAnyPermission(['HOSP_COMPTE_RENDU', 'HOSP_VIEW']) },
    typesSaisie() {
      return Object.fromEntries(Object.entries(this.d.types).filter(([c]) => c !== 'TRANSFERT'))
    }
  },
  watch: {
    hospitalisationId: { immediate: true, handler() { this.charger() } }
  },
  methods: {
    async charger() {
      try {
        this.d = (await api.custom(`hospitalisations/${this.hospitalisationId}/suivi`, 'GET')).data
      } catch (e) {
        notifier(messageErreur(e))
      }
    },
    async ajouter() {
      this.enCours = true
      try {
        await api.post(`hospitalisations/${this.hospitalisationId}/suivi`, this.f)
        this.f = { type: this.f.type, texte: '', medicament: '', dose: '', voie: '' }
        await this.charger()
      } catch (e) {
        notifier(messageErreur(e))
      } finally {
        this.enCours = false
      }
    },
    ouvrirLit() {
      this.changementLit = { lit: this.d.litsLibres[0]?.id || null, motif: '' }
    },
    async changerLit() {
      try {
        this.d = (await api.post(`hospitalisations/${this.hospitalisationId}/changer-lit`, this.changementLit)).data
        this.changementLit = null
        notifier('Patient transféré.', 'success')
        this.$emit('change')
      } catch (e) {
        notifier(messageErreur(e))
      }
    },
    async ouvrirCompteRendu() {
      this.erreur = ''
      const res = (await api.custom(`hospitalisations/${this.hospitalisationId}/compte-rendu`, 'GET')).data
      this.modes = res.modes
      this.cr = res.compteRendu || { diagnosticEntree: '', diagnosticSortie: '', resume: '', examensRealises: '', traitementSortie: '', consignes: '', modeSortie: 'GUERISON', etablissementTransfert: '', rendezVousControle: '' }
    },
    async enregistrerCompteRendu() {
      this.enCours = true
      this.erreur = ''
      try {
        this.cr = (await api.custom(`hospitalisations/${this.hospitalisationId}/compte-rendu`, 'PUT', this.cr)).data.compteRendu
        notifier('Compte-rendu enregistré.', 'success')
      } catch (e) {
        this.erreur = messageErreur(e)
      } finally {
        this.enCours = false
      }
    },
    pdfCompteRendu() {
      api.telecharger(`hospitalisations/${this.hospitalisationId}/compte-rendu/pdf`, 'compte-rendu.pdf', true).catch(e => notifier(messageErreur(e)))
    },
    badge(t) {
      return { VISITE: 'bg-primary', OBSERVATION: 'bg-info text-dark', SOIN: 'bg-success', MEDICAMENT: 'bg-warning text-dark', TRANSFERT: 'bg-secondary' }[t] || 'bg-light text-dark'
    }
  },
  emits: ['change']
}
</script>

<style scoped>
.card-suivi { background: #f8fafd; border: none; border-radius: 10px; }
.timeline li { background: #fff; border-left: 3px solid #0d6efd; border-radius: 8px; padding: 6px 10px; }
.texte { white-space: pre-line; }
.modal-backdrop-custom { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 1050; display: flex; align-items: flex-start; justify-content: center; padding: 30px 16px; overflow-y: auto; }
.modal-card { width: 100%; max-width: 820px; border-radius: 12px; }
</style>
